import { MouseEvent, ComponentProps, useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import ExploreMapPinType from '@alltrails/amplitude/enums/ExploreMapPinType';
import TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import TrailCard from '@alltrails/cards/components/TrailCard';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { TrailActivityType, trailFromAlgoliaTrail } from '@alltrails/trails';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import PaddingAwarePopup from '../PaddingAwarePopup';
import PopupCarouselControls from '../PopupCarouselControls';
import styles from './styles/ActiveTrailPopup.module.scss';

type ActiveTrailPopupProps = Pick<ComponentProps<typeof PaddingAwarePopup>, 'latitude' | 'longitude' | 'offset'> & {
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  initialTrailIndex?: number;
  isComplete: (id: number) => boolean;
  isFavorite: (id: number) => boolean;
  isVerified: (id: number) => boolean;
  onFavoriteClick: (id: number) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTrailCardViewed?: (id: number, index: number) => void;
  onTrailChange: (trail: TrailSearchResult) => void;
  onTrailNavigate?: (slug: string, event?: MouseEvent) => void;
  trails: TrailSearchResult[];
};

const ActiveTrailPopup = ({
  activityType,
  cardLocation,
  detailedCardLocation,
  initialTrailIndex = 0,
  isComplete,
  isFavorite,
  isVerified,
  latitude,
  longitude,
  offset,
  onFavoriteClick,
  onMouseEnter,
  onMouseLeave,
  onTrailChange,
  onTrailCardViewed,
  onTrailNavigate,
  trails
}: ActiveTrailPopupProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const { hasPermission } = useAuthorization();
  const languageRegionCode = useLanguageRegionCode();
  const { setOverlayActive } = useContext(MapLayerInteractionsContext);

  useEffect(() => {
    if (trails.length > 1) {
      // For multiple trail pins, when the popup is visible, block map layer events to prevent POI pins
      // (or other layers) from receiving hover/click events
      setOverlayActive('trail-popup', true);
    }

    return () => setOverlayActive('trail-popup', false);
  }, [setOverlayActive, trails.length]);

  const [index, setIndex] = useState(initialTrailIndex);
  useEffect(() => {
    setIndex(initialTrailIndex);
  }, [initialTrailIndex]);

  const onIndexChange = useCallback(
    (newIndex: number) => {
      const newTrail = trails[newIndex];
      setIndex(newIndex);
      onTrailChange(newTrail);
      let pinType: ExploreMapPinType = 'trailhead';
      if (trails.every(t => t.is_closed)) {
        pinType = 'closed';
      } else if (trails.every(t => isFavorite(t.ID))) {
        pinType = 'saved';
      } else if (trails.every(t => isComplete(t.ID))) {
        pinType = 'completed';
      }

      logExploreMapPinSelected({
        action_taken: 'toggled from trailhead pin',
        pin_type: pinType,
        trail_id: newTrail.ID
      });
    },
    [isComplete, isFavorite, onTrailChange, trails]
  );

  const getAdminCTAs = useCallback(
    (trailId: number) => {
      if (hasPermission({ permission: 'trails:manage' })) {
        return [
          {
            id: 'edit',
            label: intl.formatMessage({ defaultMessage: 'Edit' }),
            onClick: () => window.open(`/contribute/route?trail_id=${trailId}`, '_blank')
          }
        ];
      }
      return undefined;
    },
    [hasPermission, intl]
  );

  const onCardClick = useCallback(
    (result: TrailSearchResult, event?: MouseEvent) => {
      logTrailCardClicked({ trail_id: String(result.id), card_location: cardLocation, detailed_card_location: detailedCardLocation });
      onTrailNavigate?.(result.slug, event);
    },
    [cardLocation, detailedCardLocation, onTrailNavigate]
  );

  const renderTrailCard = useCallback(
    (result: TrailSearchResult) => (
      <TrailCard
        key={result.ID} // use a key to ensure the correct trail photo is used if the shown trail is changed via hovering fast enough that the TrailCard is still rendered
        adminCTAs={getAdminCTAs(result.ID)}
        className={styles.trailCard}
        variant="small"
        languageRegionCode={languageRegionCode}
        onCardMouseEnter={onMouseEnter}
        onCardMouseLeave={onMouseLeave}
        onFavoriteClick={() => onFavoriteClick(result.ID)}
        onCardClick={e => onCardClick(result, e)}
        blockNavigation={Boolean(onTrailNavigate)}
        isFavorite={isFavorite(result.ID)}
        isCompleted={isComplete(result.ID)}
        isVerified={isVerified(result.ID)}
        trail={trailFromAlgoliaTrail(result, activityType)}
        lazyLoadImages={true}
        openInNewTab={!onTrailNavigate}
      />
    ),
    [
      activityType,
      getAdminCTAs,
      isComplete,
      isFavorite,
      isVerified,
      languageRegionCode,
      onFavoriteClick,
      onCardClick,
      onMouseEnter,
      onMouseLeave,
      onTrailNavigate
    ]
  );

  const trail = trails?.[index];

  useEffect(() => {
    onTrailCardViewed?.(trail?.ID, index);
    // Only want to log this when the trail changes
  }, [trail?.ID]);

  if (!trail) {
    return null;
  }

  if (isMobile) {
    return (
      <div className={styles.mobilePopup}>
        <Carousel
          uniqueId="trail-pin-carousel"
          items={trails}
          renderItem={renderTrailCard}
          getItemKey={trail => `trail-${trail.ID}`}
          initialSlideIndex={index}
          onSlideChange={onIndexChange}
          showPaginationDots={false}
          slidesPerViewMobile={1}
        />
      </div>
    );
  }

  return (
    <PaddingAwarePopup
      className={styles.desktopPopup}
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={latitude}
      longitude={longitude}
      maxWidth="none"
      offset={offset}
    >
      <div className={styles.desktopPopupContents}>
        {renderTrailCard(trail)}
        {trails.length > 1 ? (
          <PopupCarouselControls className={styles.desktopPopupControls} index={index} onIndexChange={onIndexChange} total={trails.length} />
        ) : null}
      </div>
    </PaddingAwarePopup>
  );
};

export default ActiveTrailPopup;
